import { useEffect, useRef, useState } from 'react'
import apiService from '../services/api'

export const BANGLADESH = 'bangladesh'

export const isBangladesh = (country) =>
  (country || '').trim().toLowerCase() === BANGLADESH

/**
 * Division -> district -> thana, fetched from /api/salat/geo.
 *
 * Shared by the profile editor and the signup form so the two cannot drift.
 * They render different markup — signup uses its own styling — but the fetching,
 * the cascade and the clearing rules live here once.
 *
 * Choosing a level clears the ones below it. Without that a member could pick
 * Sylhet, then Dhaka, and submit a Sylhet thana against a Dhaka district — a
 * combination the server rejects, so the mistake would only surface on save.
 */
export function useGeoCascade(country, initial = {}) {
  const bd = isBangladesh(country)

  // `initial` seeds the profile editor with what the member already has. Signup
  // passes nothing and starts empty.
  const [division, setDivision] = useState(initial.division || '')
  const [district, setDistrict] = useState(initial.district || '')
  const [thana, setThana] = useState(initial.thana || '')

  const [divisions, setDivisions] = useState([])
  const [districts, setDistricts] = useState([])
  const [thanas, setThanas] = useState([])
  const [loadError, setLoadError] = useState(false)

  // Changing country clears any levels already chosen. Without this, switching
  // Bangladesh -> India -> Bangladesh would leave the earlier thana selected but
  // invisible, and submit it against whatever was picked next.
  //
  // Skipped on the first render, or it would wipe the values `initial` just
  // seeded for a member who already has an address.
  const previousCountry = useRef(country)
  useEffect(() => {
    if (previousCountry.current === country) return
    previousCountry.current = country
    setDivision(''); setDistrict(''); setThana('')
  }, [country])

  // Divisions are fixed, so they are fetched once when Bangladesh is chosen.
  useEffect(() => {
    if (!bd) { setDivisions([]); return }
    let cancelled = false
    apiService.getGeo()
      .then((d) => { if (!cancelled) { setDivisions(d.divisions || []); setLoadError(false) } })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [bd])

  useEffect(() => {
    if (!bd || !division) { setDistricts([]); return }
    let cancelled = false
    apiService.getGeo({ division })
      .then((d) => { if (!cancelled) { setDistricts(d.districts || []); setLoadError(false) } })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [bd, division])

  useEffect(() => {
    if (!bd || !district) { setThanas([]); return }
    let cancelled = false
    apiService.getGeo({ district })
      .then((d) => { if (!cancelled) { setThanas(d.units || []); setLoadError(false) } })
      .catch(() => { if (!cancelled) setLoadError(true) })
    return () => { cancelled = true }
  }, [bd, district])

  const chooseDivision = (v) => { setDivision(v); setDistrict(''); setThana('') }
  const chooseDistrict = (v) => { setDistrict(v); setThana('') }
  const reset = () => { setDivision(''); setDistrict(''); setThana('') }

  return {
    bd, division, district, thana,
    divisions, districts, thanas, loadError,
    chooseDivision, chooseDistrict, setThana, setDivision, setDistrict, reset,
  }
}
